import { createContext, useContext, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSource } from "@/context/SourceContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Wordmark, LogoMark } from "@/components/Logo";
import SourceSelectDialog from "@/components/SourceSelectDialog";
import { triggerHaptic } from "@/lib/haptics";
import {
  BarChart3, Package, Layers, Warehouse, Globe, Coins, Users, Truck, LogOut, Menu,
  PanelLeftClose, PanelLeftOpen, ShieldCheck, Boxes, User, Radar, Repeat, MapPin, PackagePlus,
} from "lucide-react";

const NavContext = createContext({ section: "", setSection: () => {} });
export const useNav = () => useContext(NavContext);

export const ROLE_META = {
  admin: {
    label: "Command Center", icon: ShieldCheck, tone: "text-blue-300", toneLight: "text-primary", navTestId: "admin-tabs",
    groups: [
      { title: "Operations", items: [
        { id: "overview", label: "Analytics", icon: BarChart3, testId: "tab-overview" },
        { id: "orders", label: "Orders", icon: Package, testId: "tab-orders" },
        { id: "batches", label: "Batches", icon: Layers, testId: "tab-batches" },
        { id: "warehouse", label: "Warehouse", icon: Warehouse, testId: "tab-warehouse" },
      ] },
      { title: "Network", items: [
        { id: "portals", label: "Portals", icon: Globe, testId: "tab-portals" },
        { id: "rates", label: "Rates", icon: Coins, testId: "tab-rates" },
      ] },
      { title: "Settings", items: [
        { id: "users", label: "Users", icon: Users, testId: "tab-users" },
      ] },
    ],
  },
  operations: {
    label: "Warehouse Operations", icon: Boxes, tone: "text-cyan-300", toneLight: "text-cyan-600", navTestId: "ops-tabs",
    groups: [{ title: "Operations", items: [
      { id: "shipments", label: "Shipments", icon: Boxes, testId: "ops-tab-shipments" },
      { id: "batches", label: "Batches", icon: Layers, testId: "ops-tab-batches" },
    ] }],
  },
  courier: {
    label: "Pickup Runs", icon: Truck, tone: "text-orange-300", toneLight: "text-orange-600", navTestId: "courier-tabs",
    groups: [{ title: "Field", items: [
      { id: "pickups", label: "Pickups", icon: Truck, testId: "courier-tab-pickups" },
      { id: "batches", label: "Batches", icon: Layers, testId: "courier-tab-batches" },
    ] }],
  },
  user: {
    label: "My Shipments", icon: User, tone: "text-emerald-300", toneLight: "text-emerald-600", navTestId: "user-tabs",
    groups: [{ title: "Shipping", items: [
      { id: "shipments", label: "Shipments", icon: Package, testId: "user-tab-shipments" },
    ] }],
  },
};

const GATED = ["admin", "operations", "courier"];
const initials = (n = "") => n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("") || "U";

function NavItem({ item, active, collapsed, variant, onSelect, testId }) {
  return (
    <button type="button" onClick={() => onSelect(item.id)} title={collapsed ? item.label : undefined} data-testid={testId}
      aria-current={active ? "page" : undefined}
      className={`relative group flex w-full items-center gap-3 rounded-xl py-2.5 text-sm font-medium transition-colors duration-200 ${collapsed ? "justify-center px-0" : "px-3"} ${active ? "text-white" : "text-slate-400 hover:text-white hover:bg-white/[.06]"}`}>
      {active && <motion.span layoutId={`nav-active-${variant}`} className="absolute inset-0 rounded-xl bg-primary nav-active-glow" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
      <item.icon className={`relative z-10 h-[18px] w-[18px] shrink-0 transition-transform duration-200 group-hover:scale-110 ${active ? "text-white" : "text-slate-500 group-hover:text-white"}`} />
      {!collapsed && <span className="relative z-10 truncate">{item.label}</span>}
    </button>
  );
}

function SideNav({ meta, section, setSection, collapsed, variant, onNavigate, user, source, onSwitchSource, onLogout }) {
  const tid = (id) => (variant === "desktop" ? id : `m-${id}`);
  return (
    <div className="sidebar-surface relative flex h-full flex-col text-slate-300 overflow-hidden">
      <div className={`flex h-16 items-center gap-2.5 border-b border-white/[.06] ${collapsed ? "justify-center px-0" : "px-4"}`}>
        <div className="relative shrink-0">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[.06] ring-1 ring-white/10 p-0.5"><LogoMark className="h-full w-full" /></div>
          <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[#070C16]"><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" /></span>
        </div>
        {!collapsed && (
          <div className="leading-tight min-w-0">
            <Wordmark className="text-lg" light />
            <div className={`text-[10px] font-mono-label font-semibold uppercase tracking-[0.18em] truncate ${meta.tone}`}>{meta.label}</div>
          </div>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-testid={tid(meta.navTestId)}>
        {meta.groups.map((g) => (
          <div key={g.title}>
            {!collapsed && <div className="mb-1.5 px-3 text-[10px] font-mono-label font-semibold uppercase tracking-[0.2em] text-slate-500">{g.title}</div>}
            <div className="space-y-1">
              {g.items.map((it) => (
                <NavItem key={it.id} item={it} active={section === it.id} collapsed={collapsed} variant={variant} testId={tid(it.testId)}
                  onSelect={(id) => { triggerHaptic("tap"); setSection(id); onNavigate?.(); }} />
              ))}
            </div>
          </div>
        ))}
        <div>
          {!collapsed && <div className="mb-1.5 px-3 text-[10px] font-mono-label font-semibold uppercase tracking-[0.2em] text-slate-500">Public</div>}
          <div className="space-y-1">
            <Link to="/track" target="_blank" rel="noreferrer" data-testid={tid("sidebar-track-link")} title="Public tracking"
              className={`flex items-center gap-3 rounded-xl py-2.5 text-sm font-medium text-slate-400 hover:text-white hover:bg-white/[.06] transition-colors ${collapsed ? "justify-center px-0" : "px-3"}`}>
              <MapPin className="h-[18px] w-[18px] text-slate-500" />{!collapsed && "Track a shipment"}
            </Link>
            <Link to="/book" target="_blank" rel="noreferrer" data-testid={tid("sidebar-book-link")} title="Guest booking"
              className={`flex items-center gap-3 rounded-xl py-2.5 text-sm font-medium text-slate-400 hover:text-white hover:bg-white/[.06] transition-colors ${collapsed ? "justify-center px-0" : "px-3"}`}>
              <PackagePlus className="h-[18px] w-[18px] text-slate-500" />{!collapsed && "Guest booking page"}
            </Link>
          </div>
        </div>
      </nav>

      <div className="border-t border-white/[.06] p-3 space-y-2">
        {GATED.includes(user?.role) && source && (
          <button type="button" onClick={onSwitchSource} data-testid={tid("sidebar-source-switch")} title="Switch source"
            className={`w-full flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[.04] py-2 text-left transition-colors hover:bg-white/[.09] ${collapsed ? "justify-center px-0" : "px-3"}`}>
            <span className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-300"><Radar className="h-4 w-4" /></span>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Source</div>
                  <div className="text-xs font-semibold text-white truncate" data-testid={tid("sidebar-source-label")}>{source === "ALL" ? "ShipOMS — All Sources" : source}</div>
                </div>
                <Repeat className="h-3.5 w-3.5 text-slate-500" />
              </>
            )}
          </button>
        )}
        <div className={`flex items-center gap-2.5 rounded-xl px-2 py-1.5 ${collapsed ? "justify-center flex-col" : ""}`}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-cyan-500 text-sm font-bold text-white shadow-lg shadow-primary/30">{initials(user?.name)}</div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-white">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{user?.role}</div>
            </div>
          )}
          <button type="button" onClick={onLogout} data-testid={tid("logout-button")} title="Logout"
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const { source } = useSource();
  const navigate = useNavigate();
  const role = user?.role || "user";
  const meta = ROLE_META[role] || ROLE_META.user;
  const allItems = meta.groups.flatMap((g) => g.items);
  const [section, setSectionState] = useState(() => {
    const saved = sessionStorage.getItem(`shipoms_section_${role}`);
    return allItems.some((i) => i.id === saved) ? saved : allItems[0].id;
  });
  const setSection = (s) => { sessionStorage.setItem(`shipoms_section_${role}`, s); setSectionState(s); };
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("shipoms_sidebar") === "collapsed");
  const toggleCollapsed = () => { triggerHaptic("tap"); setCollapsed((c) => { localStorage.setItem("shipoms_sidebar", c ? "open" : "collapsed"); return !c; }); };
  const [mobileOpen, setMobileOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const current = allItems.find((i) => i.id === section) || allItems[0];
  const gated = GATED.includes(role);

  const handleLogout = async () => { triggerHaptic("tap"); await logout(); navigate("/"); };
  const navProps = { meta, section, setSection, user, source, onSwitchSource: () => { triggerHaptic("tap"); setSwitching(true); setMobileOpen(false); }, onLogout: handleLogout };

  return (
    <NavContext.Provider value={{ section, setSection }}>
      <div className="min-h-screen">
        <aside className={`hidden lg:block fixed inset-y-0 left-0 z-40 transition-[width] duration-300 ease-[cubic-bezier(.22,1,.36,1)] ${collapsed ? "w-[76px]" : "w-[260px]"}`} data-testid="sidebar">
          <SideNav {...navProps} collapsed={collapsed} variant="desktop" />
          <button type="button" onClick={toggleCollapsed} data-testid="sidebar-collapse-toggle" title={collapsed ? "Expand" : "Collapse"}
            className="absolute -right-3 top-[76px] z-50 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-white text-muted-foreground shadow-md transition-all hover:scale-110 hover:text-primary">
            {collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}
          </button>
        </aside>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-[284px] border-0 bg-[#070C16] p-0 [&>button]:text-slate-400" data-testid="mobile-sidebar">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SideNav {...navProps} collapsed={false} variant="mobile" onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>

        <div className={`transition-[padding] duration-300 ease-[cubic-bezier(.22,1,.36,1)] ${collapsed ? "lg:pl-[76px]" : "lg:pl-[260px]"}`}>
          <header className="glass sticky top-0 z-30 border-b border-border/80">
            <div className="h-0.5 w-full bg-gradient-to-r from-primary via-cyan-500 to-brand-red" />
            <div className="flex h-14 items-center gap-3 px-4 sm:px-6 lg:px-8">
              <button type="button" className="lg:hidden rounded-lg p-2 text-foreground hover:bg-secondary transition-colors" onClick={() => { triggerHaptic("tap"); setMobileOpen(true); }} data-testid="mobile-nav-toggle" aria-label="Open navigation">
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0 flex-1">
                <div className={`text-[10px] font-mono-label font-semibold uppercase tracking-[0.2em] ${meta.toneLight}`}>{meta.label}</div>
                <div className="truncate font-display text-base font-bold leading-tight" data-testid="topbar-section-title">{current.label}</div>
              </div>
              {gated && source && (
                <button type="button" onClick={() => { triggerHaptic("tap"); setSwitching(true); }} data-testid="topbar-source-pill"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/10">
                  <Radar className="h-3.5 w-3.5" /> {source === "ALL" ? "All Sources" : source}
                </button>
              )}
              <div className="hidden sm:flex items-center gap-2 rounded-full border border-border bg-white/70 py-1 pl-1 pr-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-cyan-500 text-[11px] font-bold text-white">{initials(user?.name)}</div>
                <span className="text-sm font-medium" data-testid="header-user-name">{user?.name}</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{user?.role}</span>
              </div>
              <button type="button" onClick={handleLogout} data-testid="header-logout-button" title="Logout"
                className="lg:hidden rounded-lg p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </header>

          <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <AnimatePresence mode="wait">
              <motion.div key={section} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}>
                {children}
              </motion.div>
            </AnimatePresence>
          </main>
        </div>

        {switching && <SourceSelectDialog open onClose={() => setSwitching(false)} />}
      </div>
    </NavContext.Provider>
  );
}
